import type { UIMessage } from "ai"
import type { ChatTail } from "./agent-chat"
import { fetchChatTail } from "./chat-history"

/**
 * Project load speed (2026-10-17, ux-pass-3 P2): an agent chat's stored transcript, kept in this tab.
 *
 * - `prefetchChat` asks for the transcript's newest page (`…/chat-page`, decision 0160) before the chat mounts: when a project page opens on its
 *   Chat tab, in parallel with the project snapshot and the chat's code, instead of after both.
 * - `rememberChat` / `cachedChat`: the last transcript this tab showed per agent, so coming back to a chat (another
 *   tab, another agent, another project) shows it at once while the fresh copy loads.
 *
 * Memory only (never written to disk) and dropped on sign-out with the rest of the user's state (`clearChatCache`).
 * The server's copy always wins: the chat replaces a cached transcript with the one it fetches.
 */

/** The Agents SDK name of an agent's body (`<workspaceId>.<agentId>`), as `useAgent` connects to it. */
export const agentChatName = (workspaceId: string, agentId: string): string => `${workspaceId}.${agentId}`

const BODY_PATH = "/api/agents/agent-body/"

/** The body's name in a chat socket's HTTP URL (`…/api/agents/agent-body/<name>[/…][?…]`), or null. */
export const chatNameOfUrl = (url: string | null | undefined): string | null => {
  if (!url) return null
  try {
    const path = new URL(url, "http://localhost").pathname
    const at = path.indexOf(BODY_PATH)
    if (at < 0) return null
    const name = path.slice(at + BODY_PATH.length).split("/")[0]
    return name ? decodeURIComponent(name) : null
  } catch {
    return null
  }
}

/** A prefetch this old is not taken by a chat that mounts later (it fetches its own). */
export const PREFETCH_FRESH_MS = 15_000
/** Transcripts kept (the agents chatted with most recently). */
export const MAX_CACHED_CHATS = 12

interface Prefetch {
  readonly promise: Promise<ChatTail>
  readonly at: number
}

const transcripts = new Map<string, ReadonlyArray<UIMessage>>()
const prefetches = new Map<string, Prefetch>()

/** Remember what the chat shows now (the newest goes last; the oldest beyond the cap is dropped). */
export const rememberChat = (name: string, messages: ReadonlyArray<UIMessage>): void => {
  if (messages.length === 0) return
  transcripts.delete(name)
  transcripts.set(name, messages)
  while (transcripts.size > MAX_CACHED_CHATS) {
    const oldest = transcripts.keys().next().value
    if (oldest === undefined) break
    transcripts.delete(oldest)
  }
}

/** The transcript this tab last showed for the agent, or null. */
export const cachedChat = (name: string): ReadonlyArray<UIMessage> | null => transcripts.get(name) ?? null

/**
 * Start fetching the agent's stored transcript now (at most one fresh request per agent). A failure is dropped here:
 * the chat that takes the prefetch sees it and shows its own failure and Retry.
 */
export const prefetchChat = (
  name: string,
  fetchFn: typeof fetch = (...args) => fetch(...args),
  now: () => number = Date.now,
): void => {
  const existing = prefetches.get(name)
  if (existing && now() - existing.at < PREFETCH_FRESH_MS) return
  const promise = fetchChatTail(`${BODY_PATH}${encodeURIComponent(name)}`, fetchFn)
  promise.catch(() => {})
  prefetches.set(name, { promise, at: now() })
}

/** A fresh prefetch for the agent, once (the next load fetches again), or null. */
export const takePrefetchedChat = (name: string, now: () => number = Date.now): Promise<ChatTail> | null => {
  const p = prefetches.get(name)
  if (!p) return null
  prefetches.delete(name)
  return now() - p.at < PREFETCH_FRESH_MS ? p.promise : null
}

/** Forget every transcript and prefetch (sign-out, session loss). */
export const clearChatCache = (): void => {
  transcripts.clear()
  prefetches.clear()
}
